
import React, { useState, useRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Bold, Italic, List, ListOrdered, Code, Quote, Image, Link as LinkIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import remarkGfm from 'remark-gfm';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  onImageUpload?: (file: File) => void;
}

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function MarkdownEditor({ value, onChange, onImageUpload }: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<string>('edit');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertMarkdownSyntax = (syntax: { prefix: string; suffix?: string; placeholder?: string }) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;

    const selectedText = text.substring(start, end);
    const placeholderText = syntax.placeholder || selectedText || 'text';

    const insertedText = selectedText 
      ? `${syntax.prefix}${selectedText}${syntax.suffix || ''}`
      : `${syntax.prefix}${placeholderText}${syntax.suffix || ''}`;

    const newText = text.substring(0, start) + insertedText + text.substring(end);
    
    onChange(newText);
    
    // Set selection position to maintain editing flow
    setTimeout(() => {
      textarea.focus();
      if (!selectedText) {
        const newCursorPos = start + syntax.prefix.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos + placeholderText.length);
      } else {
        const newCursorPos = start + insertedText.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleImageUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      onImageUpload(file);
      
      // Clear the input value so the same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="w-full bg-white dark:bg-gray-900 rounded-md">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b px-2">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="edit">Edit</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-1 p-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ prefix: '**', suffix: '**' })}
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ prefix: '_', suffix: '_' })}
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ prefix: '- ' })}
                title="Bulleted List"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ prefix: '1. ' })}
                title="Numbered List"
              >
                <ListOrdered className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ 
                  prefix: '```javascript\n', 
                  suffix: '\n```',
                  placeholder: 'console.log("Hello world!");'
                })}
                title="Code Block"
              >
                <Code className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ prefix: '> ' })}
                title="Quote"
              >
                <Quote className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleImageUploadClick}
                title="Upload Image"
              >
                <Image className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => insertMarkdownSyntax({ 
                  prefix: '[', 
                  suffix: '](https://example.com)',
                  placeholder: 'link text'
                })}
                title="Insert Link"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        
        <TabsContent value="edit" className="p-0 mt-0">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full min-h-[400px] border-0 focus-visible:ring-0 font-mono text-sm leading-relaxed"
            placeholder="Write your markdown content here..."
          />
        </TabsContent>
        
        <TabsContent value="preview" className="p-4 mt-0 bg-gray-50 dark:bg-gray-800 min-h-[400px]">
          <div className="prose dark:prose-invert max-w-none">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                code({node, inline, className, children, ...props}: CodeProps) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={match[1]}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                }
              }}
            >
              {value}
            </ReactMarkdown>
          </div>
        </TabsContent>
      </Tabs>

      {/* Hidden file input for image uploading */}
      <input 
        type="file" 
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </div>
  );
}
